/*
 * ff-kit-addons.js — PROTOTYPE (Phase 1)
 *
 * Renders the "Build your kit" panel on the wood-frame product page. Layout is
 * modeled on mockup #3 ("Preset bundles"): pick a starting point
 * (Frame only / Complete kit), optionally add a matboard (an obvious add-on row
 * below the cards), then fine-tune glazing/backing in the "Customize" disclosure.
 *
 * Matboard now lives HERE (it used to sit in the size preview). The old
 * #ff-frame-mat-* inputs are kept in the DOM but hidden; ff-frame-renderer.js
 * still binds to them to draw the mat on the canvas, so this panel drives those
 * hidden inputs (set value + dispatch change) whenever the customer changes the
 * kit mat. That keeps the visualizer in sync without touching the renderer.
 *
 * Prices are computed client-side from the SAME width/height the visualizer
 * uses (window.FFDpoDimensions.getDimensions), reusing the per-sq-in rates +
 * SKUs supplied by the #ff-kit-addons-config JSON block in main-product.liquid.
 *
 * IMPORTANT: this is display + line-item-property only. It shows an estimate and
 * writes the selections onto the single frame cart line as properties; it does
 * NOT change the amount Shopify charges. Charging is Phase 2 (wiring the same
 * rates/SKUs into the frame's DPO price formula).
 */
(function () {
  'use strict';

  var MOUNT_ID = 'ff-kit-addons';
  var CONFIG_ID = 'ff-kit-addons-config';
  var PROP_PREFIX = 'Kit'; // line-item property namespace, e.g. "Kit Glazing"

  var state = {
    config: null,
    formId: null,
    mount: null,
    cards: {},          // presetId -> card element
    activePreset: null, // presetId or null (= custom)
    basePreset: null,   // last card they picked; its components show as "Included"
    applyingPreset: false,
    comps: {},          // componentId -> { enabled: checkbox, select: <select>, cfg }
    mat: {
      cfg: null,
      enabled: false,
      colorSku: null,
      border: null,     // { value, label }
      toggle: null,
      optionsWrap: null,
      borderSelect: null,
      swatchWrap: null,
      choice: 'none',   // wizard step 2: 'none' or 'mat'
      choiceCards: null,
      nextBtn: null
    },
    helpEl: null,
    summaryEl: null,
    totalEl: null,
    noteEl: null,
    propInputs: {},
    chipsEl: null,
    // Wizard scaffold
    progressEl: null,
    panelsById: {},
    dpoMount: null,      // step 1: hosts the relocated ITORIS DPO container
    actionsMount: null,  // review step: hosts the relocated Add-to-Cart row
    upgradeBtn: null,
    steps: [],
    stepIndex: 0,
    layout: 'wizard',
    sizeReadoutEl: null,
    wideTabsEl: null
  };

  var CLASSIC_LAYOUTS = ['wizard', 'segment', 'accordion', 'split', 'compact'];
  var WIDE_LAYOUTS = ['widepage', 'widecol', 'widedeck'];
  var KNOWN_LAYOUTS = CLASSIC_LAYOUTS.concat(WIDE_LAYOUTS);

  var LAYOUT_LABELS = {
    wizard: 'Layout A \u2014 step wizard (original, fixed)',
    segment: 'Layout B \u2014 one page, segmented Frame / Kit',
    accordion: 'Layout C \u2014 one page, accordion sections',
    split: 'Layout D \u2014 one page, two-column add-ons',
    compact: 'Layout E \u2014 shortest: hide add-ons until you pick a kit',
    widepage: 'Layout F \u2014 wide, paged options (no scroll)',
    widecol: 'Layout G \u2014 wide, preview in the middle',
    widedeck: 'Layout H \u2014 wide, tabbed deck'
  };

  function addLayoutBadge(parent) {
    if (state.layout === 'wizard') {
      return;
    }
    var badge = document.createElement('p');
    badge.className = 'ff-kit-layout-badge';
    badge.textContent = LAYOUT_LABELS[state.layout] || state.layout;
    parent.appendChild(badge);
  }

  function resolveLayout() {
    var fromUrl = '';
    try {
      fromUrl = (new URLSearchParams(window.location.search).get('kit_layout') || '').toLowerCase();
    } catch (e) {
      fromUrl = '';
    }
    var fromMount = ((state.mount && state.mount.getAttribute('data-layout')) || '').toLowerCase();
    var raw = fromUrl || fromMount || 'wizard';
    if (KNOWN_LAYOUTS.indexOf(raw) === -1) {
      return 'wizard';
    }
    return raw;
  }

  function isWideLayout() {
    return WIDE_LAYOUTS.indexOf(state.layout) !== -1;
  }

  function applyPageChrome() {
    var root = document.documentElement;
    var detail = document.querySelector('.product-detail');
    var wide = isWideLayout();
    root.classList.toggle('ff-kit-page--wide', wide);
    if (wide) {
      root.setAttribute('data-ff-kit-layout', state.layout);
    } else {
      root.removeAttribute('data-ff-kit-layout');
    }
    if (detail) {
      detail.classList.toggle('ff-kit-page-detail--wide', wide);
      if (wide) {
        detail.setAttribute('data-ff-kit-layout', state.layout);
      } else {
        detail.removeAttribute('data-ff-kit-layout');
      }
    }
    window.setTimeout(function () {
      try {
        window.dispatchEvent(new Event('resize'));
      } catch (e) {
        /* ignore */
      }
    }, 60);
  }

  function kitHasBothComponents() {
    var ids = Object.keys(state.comps).filter(function (id) {
      return state.comps[id].available !== false;
    });
    return ids.length > 0 && ids.every(function (id) {
      return state.comps[id].enabled && state.comps[id].enabled.checked;
    });
  }

  function syncSizePrompt(show) {
    var nodes = document.querySelectorAll('.ff-kit-size-prompt');
    for (var i = 0; i < nodes.length; i += 1) {
      nodes[i].hidden = !show;
    }
  }

  function formatDimPair() {
    var dims = getArtworkDims();
    if (!dims.ready) {
      return 'Size not set';
    }
    return inchesText(dims.w) + ' \u00d7 ' + inchesText(dims.h);
  }

  function inchesText(inches) {
    var eighths = Math.round(inches * 8);
    var whole = Math.floor(eighths / 8);
    var rem = eighths % 8;
    var frac = ['', '1/8', '1/4', '3/8', '1/2', '5/8', '3/4', '7/8'][rem];
    return whole + (frac ? '-' + frac : '') + '"';
  }

  // DPO's size fields hold the frame opening (artwork + mat border); prices use
  // that. The artwork size is what the customer typed.
  function getArtworkDims() {
    var api = window.FFDpoDimensions;
    var dims = api && typeof api.getArtwork === 'function' ? api.getArtwork() : null;
    var w = dims && dims.widthIn ? dims.widthIn : null;
    var h = dims && dims.heightIn ? dims.heightIn : null;
    return { w: w, h: h, ready: !!(w && h) };
  }

  function matBorderIn() {
    return state.mat.border ? parseFloat(state.mat.border.value) || 0 : 0;
  }

  // Frame size line under the artwork size. A mat grows the frame, and the
  // frame, glazing and backing are all priced at that larger size, so the
  // price jump on the mat step needs a visible reason.
  function frameLineHtml() {
    var art = getArtworkDims();
    var frame = getDims();
    if (!art.ready || !frame.ready) {
      return '';
    }
    var grown = state.mat.enabled && (frame.w > art.w || frame.h > art.h);
    var why = grown
      ? 'artwork + ' + inchesText(matBorderIn()) + ' mat on each side'
      : 'same as artwork, no mat';
    return '<span class="ff-kit-size-readout__frame' + (grown ? ' is-grown' : '') + '">' +
      'Frame size <strong>' + inchesText(frame.w) + ' \u00d7 ' + inchesText(frame.h) + '</strong>' +
      ' <span class="ff-kit-size-readout__why">(' + why + ')</span></span>';
  }

  function updateMatGrowNote() {
    var el = state.mat.growNote;
    if (!el) {
      return;
    }
    var art = getArtworkDims();
    var b = matBorderIn();
    if (!art.ready || !b) {
      el.hidden = true;
      return;
    }
    var fw = inchesText(art.w + b * 2);
    var fh = inchesText(art.h + b * 2);
    var artTxt = inchesText(art.w) + ' \u00d7 ' + inchesText(art.h);
    el.hidden = false;
    el.innerHTML = state.mat.enabled
      ? 'Your frame is now <strong>' + fw + ' \u00d7 ' + fh + '</strong> (was ' + artTxt +
        ') to fit the ' + inchesText(b) + ' mat. The frame, glazing and backing are priced at the larger size, so most of the price change is the bigger frame, not the mat.'
      : 'Adding a ' + inchesText(b) + ' mat makes the frame <strong>' + fw + ' \u00d7 ' + fh +
        '</strong> (from ' + artTxt + '). The frame, glazing and backing are priced at that larger size.';
  }

  function updateSizeReadout() {
    updateMatGrowNote();
    if (!state.sizeReadoutEl) {
      return;
    }
    var frameHtml = frameLineHtml();
    state.sizeReadoutEl.innerHTML =
      '<span class="ff-kit-size-readout__art">Artwork size <strong>' +
      formatDimPair() +
      '</strong> <button type="button" class="ff-wizard__btn--link ff-kit-change-size">Change</button></span>' +
      frameHtml;
    // recompute() re-renders this often, so the highlight is held by time
    // rather than by a one-shot CSS animation.
    var frameKey = frameHtml.replace(/<[^>]+>/g, '');
    if (state.lastFrameKey && frameKey && frameKey !== state.lastFrameKey) {
      state.frameFlashUntil = Date.now() + 1800;
      clearTimeout(state.frameFlashTimer);
      state.frameFlashTimer = setTimeout(updateSizeReadout, 1850);
    }
    if (frameKey) {
      state.lastFrameKey = frameKey;
    }
    var line = state.sizeReadoutEl.querySelector('.ff-kit-size-readout__frame');
    if (line && state.frameFlashUntil && Date.now() < state.frameFlashUntil) {
      line.classList.add('is-flash');
    }
    var btn = state.sizeReadoutEl.querySelector('.ff-kit-change-size');
    if (btn) {
      btn.addEventListener('click', function () {
        if (state.layout === 'wizard') {
          goToStep('size');
        } else if (state.layout === 'widepage' || state.layout === 'widedeck') {
          goToStep('kit');
        } else {
          var mount = state.dpoMount;
          if (mount && mount.scrollIntoView) {
            try {
              mount.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            } catch (e) {
              mount.scrollIntoView();
            }
          }
        }
      });
    }
  }

  function syncUpgradeVisibility() {
    if (!state.upgradeBtn) {
      return;
    }
    // Only offer the switch when they do NOT already have glazing + backing.
    // Use both hidden + a class: .ff-wizard__upgrade { display:block } otherwise
    // wins over the UA [hidden] rule and the CTA stays visible on Complete kit.
    var hide = kitHasBothComponents() || state.activePreset === 'complete';
    state.upgradeBtn.hidden = hide;
    state.upgradeBtn.setAttribute('aria-hidden', hide ? 'true' : 'false');
    if (hide) {
      state.upgradeBtn.style.display = 'none';
    } else {
      state.upgradeBtn.style.display = '';
    }
  }

  function reconcilePresetFromComps() {
    var complete = (state.config.presets || []).filter(function (p) {
      return p.id === 'complete';
    })[0];
    if (!complete) {
      return;
    }
    var want = complete.components || {};
    var matches = true;
    Object.keys(state.comps).forEach(function (id) {
      var entry = state.comps[id];
      var sku = entry.available === false ? null : want[id];
      if (sku) {
        if (!entry.enabled.checked) {
          matches = false;
          return;
        }
        var opt = (entry.cfg.options || [])[parseInt(entry.select.value, 10)];
        if (!opt || opt.sku !== sku) {
          matches = false;
        }
      } else if (entry.enabled.checked) {
        matches = false;
      }
    });
    if (matches && state.activePreset !== 'complete') {
      state.activePreset = 'complete';
      state.basePreset = 'complete';
      setHelp('Complete kit — glazing, acid-free backing + hanging hardware. Fine-tune or remove them in the Options step.');
      paintCardSelection();
    }
  }

  function parseConfig() {
    var el = document.getElementById(CONFIG_ID);
    if (!el) {
      return null;
    }
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function money(n) {
    if (typeof n !== 'number' || isNaN(n)) {
      return '$0.00';
    }
    return '$' + n.toFixed(2);
  }

  function getDims() {
    var dims = null;
    if (window.FFDpoDimensions && typeof window.FFDpoDimensions.getDimensions === 'function') {
      dims = window.FFDpoDimensions.getDimensions();
    }
    var w = dims && dims.widthIn ? dims.widthIn : null;
    var h = dims && dims.heightIn ? dims.heightIn : null;
    return { w: w, h: h, ready: !!(w && h) };
  }

  function areaPrice(rate, w, h) {
    if (!rate || !w || !h) {
      return 0;
    }
    return rate * w * h;
  }

  function isFloaterPreview() {
    var el = document.getElementById('ff-frame-preview');
    return !!(el && el.getAttribute('data-is-floater') === 'true');
  }

  function matColors() {
    return (state.mat.cfg && state.mat.cfg.colors) || [];
  }

  function matBorders() {
    return (state.mat.cfg && state.mat.cfg.borders) || [];
  }

  function colorBySku(sku) {
    var colors = matColors();
    for (var i = 0; i < colors.length; i += 1) {
      if (colors[i].sku === sku) {
        return colors[i];
      }
    }
    return null;
  }

  function defaultColor() {
    var colors = matColors();
    for (var i = 0; i < colors.length; i += 1) {
      if (colors[i].default) {
        return colors[i];
      }
    }
    return colors[0] || null;
  }

  function defaultBorder() {
    var borders = matBorders();
    for (var i = 0; i < borders.length; i += 1) {
      if (borders[i].default) {
        return borders[i];
      }
    }
    return borders[0] || null;
  }

  function isDarkHex(hex) {
    if (!hex) {
      return false;
    }
    var m = String(hex).replace('#', '');
    if (m.length !== 6) {
      return false;
    }
    var r = parseInt(m.substr(0, 2), 16);
    var g = parseInt(m.substr(2, 2), 16);
    var b = parseInt(m.substr(4, 2), 16);
    return 0.299 * r + 0.587 * g + 0.114 * b < 140;
  }

  // ---- Preview mat drivers ------------------------------------------------
  // The renderer binds to these hidden inputs; drive them + fire native change
  // so the canvas keeps rendering the mat the customer picked in the kit.

  function fireChange(el) {
    if (!el) {
      return;
    }
    var evt;
    try {
      evt = new Event('change', { bubbles: true });
    } catch (e) {
      evt = document.createEvent('HTMLEvents');
      evt.initEvent('change', true, false);
    }
    el.dispatchEvent(evt);
    if (window.jQuery) {
      try {
        window.jQuery(el).trigger('change');
      } catch (e2) {
        /* no-op */
      }
    }
  }

  // Rebuild the hidden mat selects from the shared config so their option order
  // matches the kit swatches and the renderer draws the exact chosen color.
  function syncHiddenMatOptions() {
    var colorSel = document.getElementById('ff-frame-mat-color');
    if (colorSel) {
      colorSel.innerHTML = '';
      matColors().forEach(function (c) {
        var o = document.createElement('option');
        o.value = c.swatch;
        o.setAttribute('data-swatch', c.swatch);
        o.setAttribute('data-sku', c.sku);
        o.textContent = c.name;
        colorSel.appendChild(o);
      });
    }
    var borderSel = document.getElementById('ff-frame-mat-border');
    if (borderSel) {
      borderSel.innerHTML = '';
      matBorders().forEach(function (b) {
        var o = document.createElement('option');
        o.value = String(b.value);
        o.textContent = b.label;
        if (b.default) {
          o.selected = true;
        }
        borderSel.appendChild(o);
      });
    }
  }

  function driveMatColor(sku) {
    var sel = document.getElementById('ff-frame-mat-color');
    if (!sel) {
      return;
    }
    for (var i = 0; i < sel.options.length; i += 1) {
      if (sel.options[i].getAttribute('data-sku') === sku) {
        if (sel.selectedIndex !== i) {
          sel.selectedIndex = i;
        }
        break;
      }
    }
    fireChange(sel);
  }

  function driveMatBorder(value) {
    var sel = document.getElementById('ff-frame-mat-border');
    if (!sel) {
      return;
    }
    sel.value = String(value);
    fireChange(sel);
  }

  function driveMatEnabled(on) {
    var cb = document.getElementById('ff-frame-mat-enabled');
    if (!cb) {
      return;
    }
    if (cb.checked !== on) {
      cb.checked = on;
      fireChange(cb);
    }
  }

  // Push the whole kit mat state onto the hidden preview inputs.
  function syncPreviewMat() {
    if (state.mat.enabled) {
      if (state.mat.border) {
        driveMatBorder(state.mat.border.value);
      }
      if (state.mat.colorSku) {
        driveMatColor(state.mat.colorSku);
      }
      driveMatEnabled(true);
    } else {
      driveMatEnabled(false);
    }
  }

  // ---- DPO bridge ----------------------------------------------------------
  // The ITORIS DPO dropdowns are what Shopify charges for and what becomes the
  // component cart lines. A component (or the mat) with a "dpoField" selects the
  // matching option in the DPO dropdown whose label is that field name, and the
  // DPO dropdown itself is hidden so the customer only picks once, here.
  // Options match by their "dpo" label prefix, or by SKU at the start of the
  // DPO option text (matboard options read "C3290  Snow White ...").
  // Kit choices with no matching DPO option are hidden: they would not be charged.

  function dpoRoot() {
    return document.getElementById('itoris_dynamicproductoptions');
  }

  function findDpoField(title) {
    var root = dpoRoot();
    if (!root || !title) {
      return null;
    }
    var want = String(title).trim().toLowerCase();
    var fields = root.querySelectorAll('.field');
    for (var i = 0; i < fields.length; i += 1) {
      var label = fields[i].querySelector('label');
      var select = fields[i].querySelector('select');
      if (label && select && label.textContent.trim().toLowerCase() === want) {
        return { field: fields[i], select: select };
      }
    }
    return null;
  }

  function dpoOptionText(o) {
    return (o.textContent || '')
      .replace(/\s+/g, ' ')
      .replace(/\s*\+\s*\$[\d.,]+\s*$/, '')
      .trim()
      .toLowerCase();
  }

  // SKU of the product linked to a DPO option, from DPO's own config.
  function dpoLinkedSku(optionValue) {
    var cfg = window.dpoOptions;
    var fields = cfg && cfg.options;
    if (!fields || !fields.length) {
      return null;
    }
    for (var f = 0; f < fields.length; f += 1) {
      var items = fields[f] && fields[f].items;
      for (var i = 0; items && i < items.length; i += 1) {
        if (String(items[i].option_type_id) === String(optionValue)) {
          return items[i].product_sku ? String(items[i].product_sku).toLowerCase() : null;
        }
      }
    }
    return null;
  }

  function findDpoOption(select, opt) {
    if (!select || !opt) {
      return null;
    }
    var byLabel = opt.dpo ? String(opt.dpo).toLowerCase() : null;
    var sku = opt.sku ? String(opt.sku).toLowerCase() : null;
    for (var i = 0; i < select.options.length; i += 1) {
      var o = select.options[i];
      if (!o.value) {
        continue;
      }
      var t = dpoOptionText(o);
      if (byLabel ? t.indexOf(byLabel) === 0 : sku && (t === sku || t.indexOf(sku + ' ') === 0)) {
        return o;
      }
      if (sku && dpoLinkedSku(o.value) === sku) {
        return o;
      }
    }
    return null;
  }

  // DPO rewrites inline styles when it re-evaluates its fields, so hide through
  // a stylesheet keyed to the select id instead. Off-screen, not display:none:
  // DPO resets fields it cannot see.
  var OFFSTAGE_RULE =
    '{position:absolute !important;left:-10000px !important;top:0 !important;' +
    'width:1px !important;height:1px !important;overflow:hidden !important;' +
    'clip-path:inset(50%) !important}';
  var hiddenDpoIds = {};
  function hideDpoField(dpo) {
    var id = dpo.select.id;
    if (!id || hiddenDpoIds[id]) {
      return;
    }
    hiddenDpoIds[id] = true;
    var styleEl = document.getElementById('ff-kit-dpo-hide');
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = 'ff-kit-dpo-hide';
      document.head.appendChild(styleEl);
    }
    var sel = '#' + id;
    styleEl.appendChild(document.createTextNode(
      '#itoris_dynamicproductoptions .fieldset:has(' + sel + '),' +
      '#itoris_dynamicproductoptions .legend:has(+ .fieldset ' + sel + '),' +
        '#itoris_dynamicproductoptions .field:has(> .control > ' + sel + ')' +
        OFFSTAGE_RULE
    ));
  }

  // Runs on every recompute; a no-op until DPO has rendered the size fields.
  function applyDpoAvailability() {
    if (!dpoRoot() || !getDims().ready) {
      return;
    }
    Object.keys(state.comps).forEach(function (id) {
      var entry = state.comps[id];
      if (!entry.cfg.dpoField) {
        return;
      }
      entry.dpo = findDpoField(entry.cfg.dpoField);
      if (entry.dpo) {
        hideDpoField(entry.dpo);
      }
      var firstOk = -1;
      var opts = entry.cfg.options || [];
      for (var i = 0; i < entry.select.options.length; i += 1) {
        var el = entry.select.options[i];
        var ok = !!(entry.dpo && findDpoOption(entry.dpo.select, opts[parseInt(el.value, 10)]));
        el.hidden = !ok;
        el.disabled = !ok;
        if (ok && firstOk === -1) {
          firstOk = i;
        }
      }
      entry.available = firstOk !== -1;
      entry.row.hidden = !entry.available;
      if (!entry.available) {
        entry.enabled.checked = false;
        entry.optionsWrap.hidden = true;
        return;
      }
      if (entry.select.options[entry.select.selectedIndex].disabled) {
        entry.select.selectedIndex = firstOk;
      }
      // DPO draws (and sometimes redraws) fields after the preset was applied,
      // so keep the kit card's choice in place until the customer changes it.
      if (!entry.userSet) {
        adoptPresetChoice(id, entry);
      }
    });

    var matCfg = state.mat.cfg;
    if (matCfg && matCfg.dpoField) {
      state.mat.dpo = findDpoField(matCfg.dpoField);
      var anyColor = false;
      if (state.mat.swatchWrap) {
        var sws = state.mat.swatchWrap.querySelectorAll('.ff-kit-swatch');
        for (var s = 0; s < sws.length; s += 1) {
          var okColor = !!(state.mat.dpo && findDpoOption(state.mat.dpo.select, { sku: sws[s].getAttribute('data-sku') }));
          sws[s].hidden = !okColor;
          anyColor = anyColor || okColor;
        }
      }
      if (state.mat.colorSku && state.mat.dpo && !findDpoOption(state.mat.dpo.select, { sku: state.mat.colorSku })) {
        var first = matColors().filter(function (c) {
          return findDpoOption(state.mat.dpo.select, { sku: c.sku });
        })[0];
        state.mat.colorSku = first ? first.sku : null;
        paintSwatchSelection();
      }
      if (!anyColor && state.mat.enabled) {
        setMatEnabled(false, false);
      }
      if (state.mat.dpo) {
        hideDpoField(state.mat.dpo);
      }
    }
  }

  function setDpoValue(select, value) {
    if (select.value === value) {
      return;
    }
    select.value = value;
    fireChange(select);
  }

  function adoptPresetChoice(id, entry) {
    var preset = basePresetCfg();
    var sku = preset && preset.components ? preset.components[id] : null;
    if (!sku) {
      return;
    }
    (entry.cfg.options || []).forEach(function (opt, i) {
      if (opt.sku === sku && !entry.select.options[i].disabled) {
        entry.select.value = String(i);
        entry.enabled.checked = true;
        entry.optionsWrap.hidden = false;
      }
    });
  }

  // Found is not enough: DPO fills in the linked-product SKUs a beat after it
  // draws a dropdown, and SKU-matched items stay unavailable until then.
  function allDpoFieldsReady() {
    return Object.keys(state.comps).every(function (id) {
      var entry = state.comps[id];
      return !entry.cfg.dpoField || (!!entry.dpo && entry.available === true);
    }) && (!state.mat.cfg || !state.mat.cfg.dpoField || !!state.mat.dpo);
  }

  function syncDpo() {
    Object.keys(state.comps).forEach(function (id) {
      var entry = state.comps[id];
      if (!entry.dpo) {
        return;
      }
      var want = '';
      if (entry.enabled.checked) {
        var match = findDpoOption(entry.dpo.select, (entry.cfg.options || [])[parseInt(entry.select.value, 10)]);
        want = match ? match.value : '';
      }
      setDpoValue(entry.dpo.select, want);
    });
    if (state.mat.dpo) {
      var matWant = '';
      if (state.mat.enabled && state.mat.colorSku) {
        var m = findDpoOption(state.mat.dpo.select, { sku: state.mat.colorSku });
        matWant = m ? m.value : '';
      }
      setDpoValue(state.mat.dpo.select, matWant);
    }
  }

  // ---- UI ------------------------------------------------------------------

  function makePanel(id, title) {
    var panel = document.createElement('div');
    panel.className = 'ff-wizard__panel';
    panel.setAttribute('data-step', id);

    var h = document.createElement('div');
    h.className = 'ff-wizard__panel-title';
    h.textContent = title;
    panel.appendChild(h);

    var body = document.createElement('div');
    body.className = 'ff-wizard__panel-body';
    panel.appendChild(body);

    state.panelsById[id] = panel;
    return { panel: panel, body: body };
  }

  function stepLabel(text) {
    var el = document.createElement('span');
    el.className = 'ff-wizard__label';
    el.textContent = text;
    return el;
  }

  function navRow(opts) {
    var nav = document.createElement('div');
    nav.className = 'ff-wizard__nav';

    if (opts.back) {
      var back = document.createElement('button');
      back.type = 'button';
      back.className = 'ff-wizard__btn ff-wizard__btn--alt';
      back.innerHTML = '\u2190 Back';
      back.addEventListener('click', goPrev);
      nav.appendChild(back);
    }
    if (opts.next) {
      var next = document.createElement('button');
      next.type = 'button';
      next.className = 'ff-wizard__btn';
      next.textContent = opts.nextLabel || 'Continue \u2192';
      next.addEventListener('click', goNext);
      nav.appendChild(next);
    }
    return nav;
  }

  function wrapAccordion(title, open) {
    var det = document.createElement('details');
    det.className = 'ff-kit-acc';
    if (open) {
      det.open = true;
    }
    var sum = document.createElement('summary');
    sum.className = 'ff-kit-acc__sum';
    sum.textContent = title;
    var body = document.createElement('div');
    body.className = 'ff-kit-acc__body';
    det.appendChild(sum);
    det.appendChild(body);
    return { wrap: det, body: body };
  }

  function buildSegmentedControl(parent) {
    var wrap = document.createElement('div');
    wrap.className = 'ff-kit-segment';
    (state.config.presets || []).forEach(function (preset) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ff-kit-segment__btn';
      btn.setAttribute('data-preset', preset.id);
      btn.innerHTML =
        '<strong>' + preset.label + '</strong>' +
        '<span>' + (preset.tagline || '') + '</span>';
      btn.addEventListener('click', function () {
        applyPreset(preset);
      });
      wrap.appendChild(btn);
      state.cards[preset.id] = btn;
    });
    parent.appendChild(wrap);
  }

  function buildFlatUi(style) {
    var cfg = state.config;
    var mount = state.mount;
    state.dpoMount = null;
    state.actionsMount = null;
    state.panelsById = {};

    addLayoutBadge(mount);

    var intro = document.createElement('p');
    intro.className = 'ff-kit-flat__intro';
    if (style === 'compact') {
      intro.textContent = 'Pick frame only to add to cart now, or a complete kit to add glazing, backing, and an optional mat.';
    } else {
      intro.textContent = 'Choose frame only or a complete kit. Size stays at the top so you can add to cart without scrolling.';
    }
    mount.appendChild(intro);

    if (style === 'segment' || style === 'compact') {
      buildSegmentedControl(mount);
    } else {
      mount.appendChild(stepLabel('Frame only or complete kit?'));
      buildPresetCards(mount);
    }

    var help = document.createElement('p');
    help.className = 'ff-kit-addons__help';
    mount.appendChild(help);
    state.helpEl = help;

    var sizeBox = document.createElement('div');
    sizeBox.className = 'ff-kit-flat__size';
    var sizeInner;
    if (style === 'accordion') {
      var accSize = wrapAccordion('Artwork size', true);
      sizeBox.appendChild(accSize.wrap);
      sizeInner = accSize.body;
    } else {
      sizeBox.appendChild(stepLabel('Artwork size'));
      sizeInner = sizeBox;
    }
    var dpoMount = document.createElement('div');
    dpoMount.className = 'ff-wizard__dpo-mount';
    sizeInner.appendChild(dpoMount);
    state.dpoMount = dpoMount;
    mount.appendChild(sizeBox);

    var extras = document.createElement('div');
    extras.className = 'ff-kit-flat__extras ff-kit-flat__extras--' + style;

    var matHost = extras;
    var optHost = extras;
    if (style === 'accordion') {
      var accMat = wrapAccordion('Matboard (optional)', false);
      var accOpt = wrapAccordion('Glazing & backing', false);
      extras.appendChild(accMat.wrap);
      extras.appendChild(accOpt.wrap);
      matHost = accMat.body;
      optHost = accOpt.body;
    } else if (style === 'split') {
      extras.appendChild(stepLabel('Add-ons'));
    }

    var compBody = document.createElement('div');
    compBody.className = 'ff-kit-addons__components-body';
    (cfg.components || []).forEach(function (comp) {
      compBody.appendChild(buildComponentRow(comp));
    });
    optHost.appendChild(compBody);

    if (cfg.mat && !isFloaterPreview()) {
      var matAdd = document.createElement('div');
      matAdd.className = 'ff-kit-addons__matadd';
      matAdd.appendChild(buildMatRow(cfg.mat));
      matHost.appendChild(matAdd);
    } else {
      state.mat.cfg = null;
    }
    mount.appendChild(extras);

    var summary = document.createElement('ul');
    summary.className = 'ff-kit-addons__summary';
    mount.appendChild(summary);
    state.summaryEl = summary;

    var totalRow = document.createElement('div');
    totalRow.className = 'ff-kit-addons__total';
    totalRow.innerHTML =
      '<span>Extra charges</span><span class="ff-kit-addons__total-value">$0.00</span>';
    mount.appendChild(totalRow);
    state.totalEl = totalRow.querySelector('.ff-kit-addons__total-value');

    var upgrade = document.createElement('button');
    upgrade.type = 'button';
    upgrade.className = 'ff-wizard__upgrade';
    upgrade.textContent = 'Want glazing + hardware? Switch to the Complete kit';
    upgrade.addEventListener('click', function () {
      var complete = (state.config.presets || []).filter(function (p) {
        return p.id === 'complete';
      })[0];
      if (complete) {
        applyPreset(complete);
      }
    });
    state.upgradeBtn = upgrade;
    mount.appendChild(upgrade);

    var note = document.createElement('p');
    note.className = 'ff-kit-addons__note';
    note.textContent = cfg.note || '';
    mount.appendChild(note);
    state.noteEl = note;

    syncSizePrompt(true);
    ensureChips();
    mount.hidden = false;
    relocateExternals();
  }

  function buildUi() {
    var cfg = state.config;
    var mount = state.mount;
    state.layout = resolveLayout();
    mount.innerHTML = '';
    mount.className = 'ff-kit-addons ff-kit-layout--' + state.layout;
    if (state.layout === 'wizard') {
      mount.classList.add('ff-wizard');
    } else if (isWideLayout()) {
      mount.classList.add('ff-kit-wide', 'ff-kit-flat');
      if (state.layout === 'widepage' || state.layout === 'widedeck') {
        mount.classList.add('ff-wizard');
      }
    } else {
      mount.classList.add('ff-kit-flat');
    }
    applyPageChrome();
    if (state.layout === 'widepage') {
      buildWidePagedUi('pages');
      return;
    }
    if (state.layout === 'widedeck') {
      buildWidePagedUi('tabs');
      return;
    }
    if (state.layout === 'widecol') {
      buildWideColUi();
      return;
    }
    if (state.layout !== 'wizard') {
      buildFlatUi(state.layout);
      return;
    }
    state.panelsById = {};

    addLayoutBadge(mount);

    var progress = document.createElement('div');
    progress.className = 'ff-wizard__progress';
    mount.appendChild(progress);
    state.progressEl = progress;

    var barReadout = document.createElement('p');
    barReadout.className = 'ff-kit-size-readout ff-kit-size-readout--bar';
    barReadout.hidden = true;
    mount.appendChild(barReadout);
    state.sizeReadoutEl = barReadout;

    var panels = document.createElement('div');
    panels.className = 'ff-wizard__panels';
    mount.appendChild(panels);

    // STEP 1 — Kit first, then size (so frame-only can add to cart immediately)
    var s1 = makePanel('size', 'Size & kit');
    s1.body.appendChild(stepLabel('Frame only or complete kit?'));
    buildPresetCards(s1.body);

    s1.body.appendChild(stepLabel('Enter your artwork size'));
    var dpoMount = document.createElement('div');
    dpoMount.className = 'ff-wizard__dpo-mount';
    s1.body.appendChild(dpoMount);
    state.dpoMount = dpoMount;

    var help = document.createElement('p');
    help.className = 'ff-kit-addons__help';
    s1.body.appendChild(help);
    state.helpEl = help;

    s1.body.appendChild(navRow({ next: true }));
    panels.appendChild(s1.panel);

    // STEP 2 — Matboard (offered to everyone, incl. Frame only) --------------
    var s2 = makePanel('mat', 'Matboard');
    s2.body.appendChild(stepLabel('Add a matboard?'));
    if (cfg.mat && !isFloaterPreview()) {
      s2.body.appendChild(buildMatChoice());
      var grow = document.createElement('p');
      grow.className = 'ff-kit-matgrow';
      grow.hidden = true;
      s2.body.appendChild(grow);
      state.mat.growNote = grow;
      var matAdd = document.createElement('div');
      matAdd.className = 'ff-kit-addons__matadd ff-kit-addons__matadd--choice';
      matAdd.appendChild(buildMatRow(cfg.mat));
      s2.body.appendChild(matAdd);
    } else {
      // Floater frames don't take a mat; drop it from the model entirely.
      state.mat.cfg = null;
      var noMat = document.createElement('p');
      noMat.className = 'ff-kit-addons__help';
      noMat.textContent = 'A matboard isn\u2019t available for this frame.';
      s2.body.appendChild(noMat);
    }
    var matNav = navRow({ back: true, next: true });
    state.mat.nextBtn = state.mat.cfg ? matNav.querySelector('.ff-wizard__btn:not(.ff-wizard__btn--alt)') : null;
    s2.body.appendChild(matNav);
    panels.appendChild(s2.panel);
    paintMatChoice();

    // STEP 3 — Options: glazing + backing (only for Complete/custom kit) ------
    var s3 = makePanel('options', 'Options');
    s3.body.appendChild(stepLabel('Glazing, backing & hardware'));
    var compBody = document.createElement('div');
    compBody.className = 'ff-kit-addons__components-body';
    s3.body.appendChild(compBody);
    (cfg.components || []).forEach(function (comp) {
      compBody.appendChild(buildComponentRow(comp));
    });
    s3.body.appendChild(navRow({ back: true, next: true }));
    panels.appendChild(s3.panel);

    // STEP 4 — Review & add to cart ------------------------------------------
    var s4 = makePanel('review', 'Review');
    var summary = document.createElement('ul');
    summary.className = 'ff-kit-addons__summary';
    s4.body.appendChild(summary);
    state.summaryEl = summary;

    var totalRow = document.createElement('div');
    totalRow.className = 'ff-kit-addons__total';
    totalRow.innerHTML =
      '<span>Extra charges</span><span class="ff-kit-addons__total-value">$0.00</span>';
    s4.body.appendChild(totalRow);
    state.totalEl = totalRow.querySelector('.ff-kit-addons__total-value');

    // Frame-only customers can jump to the Complete kit right here.
    var upgrade = document.createElement('button');
    upgrade.type = 'button';
    upgrade.className = 'ff-wizard__upgrade';
    upgrade.textContent = 'Want a matboard, glazing, backing or hardware? Build a complete kit';
    upgrade.addEventListener('click', function () {
      var complete = (state.config.presets || []).filter(function (p) {
        return p.id === 'complete';
      })[0];
      if (complete) {
        applyPreset(complete);
        goToStep(state.mat.cfg ? 'mat' : 'options');
      }
    });
    state.upgradeBtn = upgrade;
    s4.body.appendChild(upgrade);

    // The native Add-to-Cart row is relocated here (see relocateExternals).
    var actions = document.createElement('div');
    actions.className = 'ff-wizard__actions-mount';
    s4.body.appendChild(actions);
    state.actionsMount = actions;

    var note = document.createElement('p');
    note.className = 'ff-kit-addons__note';
    note.textContent = cfg.note || '';
    s4.body.appendChild(note);
    state.noteEl = note;

    var revNav = document.createElement('div');
    revNav.className = 'ff-wizard__nav';
    var back4 = document.createElement('button');
    back4.type = 'button';
    back4.className = 'ff-wizard__btn ff-wizard__btn--alt';
    back4.innerHTML = '\u2190 Back';
    back4.addEventListener('click', goPrev);
    revNav.appendChild(back4);
    var editStart = document.createElement('button');
    editStart.type = 'button';
    editStart.className = 'ff-wizard__btn ff-wizard__btn--link';
    editStart.textContent = 'Edit from start';
    editStart.addEventListener('click', function () {
      goToStep('size');
    });
    revNav.appendChild(editStart);
    s4.body.appendChild(revNav);
    panels.appendChild(s4.panel);

    ensureChips();
    mount.hidden = false;

    state.steps = computeSteps();
    state.stepIndex = 0;
    showStep(true);
    relocateExternals();
  }

  // ---- Step flow -----------------------------------------------------------

  function hasOptionsStep() {
    if (state.activePreset === 'complete') {
      return true;
    }
    var any = false;
    Object.keys(state.comps).forEach(function (id) {
      if (state.comps[id].enabled && state.comps[id].enabled.checked) {
        any = true;
      }
    });
    return any;
  }

  // Frame only goes straight from size to review. Any kit (Complete, or a custom
  // mix after editing it) walks mat -> options before review.
  function computeSteps() {
    if (state.layout === 'widepage' || state.layout === 'widedeck') {
      return ['kit', 'addons', 'review'];
    }
    if (state.layout === 'wizard') {
      return state.activePreset === 'frame-only'
        ? ['size', 'review']
        : ['size', 'mat', 'options', 'review'];
    }
    var steps = ['size', 'mat'];
    if (hasOptionsStep()) {
      steps.push('options');
    }
    steps.push('review');
    return steps;
  }

  function refreshSteps() {
    var current = state.steps[state.stepIndex];
    state.steps = computeSteps();
    var idx = state.steps.indexOf(current);
    state.stepIndex = idx >= 0 ? idx : Math.min(state.stepIndex, state.steps.length - 1);
    updateProgress();
  }

  function goToStep(id) {
    state.steps = computeSteps();
    var idx = state.steps.indexOf(id);
    state.stepIndex = idx >= 0 ? idx : 0;
    showStep();
  }

  function goNext() {
    state.steps = computeSteps();
    if (state.steps[state.stepIndex] === 'mat' && state.mat.nextBtn && !state.mat.choice) {
      return;
    }
    if (state.stepIndex < state.steps.length - 1) {
      state.stepIndex += 1;
      showStep();
    }
  }

  function goPrev() {
    state.steps = computeSteps();
    if (state.stepIndex > 0) {
      state.stepIndex -= 1;
      showStep();
    }
  }

  function showStep(initial) {
    var activeId = state.steps[state.stepIndex];
    // DPO resets any of its fields it finds display:none back to their defaults
    // (5 x 5 for size) whenever another option changes. Inactive panels hold
    // those fields, so they go off-screen rather than hidden.
    Object.keys(state.panelsById).forEach(function (id) {
      var panel = state.panelsById[id];
      var off = id !== activeId;
      panel.hidden = false;
      panel.classList.toggle('ff-offstage', off);
      panel.inert = off;
      panel.setAttribute('aria-hidden', off ? 'true' : 'false');
    });
    updateProgress();
    updateWideTabs();
    syncSizePrompt(activeId === 'size' || activeId === 'kit');
    if (state.sizeReadoutEl) {
      state.sizeReadoutEl.hidden = activeId === 'size' || activeId === 'kit';
    }
    updateSizeReadout();
    if (activeId === 'review') {
      recompute();
    }
    // Do not scroll the page — that hid the header, title, and size preview.
    if (!initial && state.mount && state.mount.scrollIntoView) {
      try {
        state.mount.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      } catch (e) {
        /* keep viewport where it is */
      }
    }
  }

  function updateProgress() {
    if (!state.progressEl) {
      return;
    }
    var n = state.steps.length;
    var i = state.stepIndex;
    var html =
      '<span class="ff-wizard__progress-text">Step ' + (i + 1) + ' of ' + n + '</span>' +
      '<span class="ff-wizard__bars">';
    for (var s = 0; s < n; s += 1) {
      html += '<span class="ff-wizard__bar' + (s <= i ? ' is-done' : '') + '"></span>';
    }
    html += '</span>';
    state.progressEl.innerHTML = html;

    syncUpgradeVisibility();
  }

  function buildWideTabs(parent, items) {
    var nav = document.createElement('div');
    nav.className = 'ff-kit-wide__tabs';
    items.forEach(function (item) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ff-kit-wide__tab';
      btn.setAttribute('data-step', item.id);
      btn.textContent = item.label;
      btn.addEventListener('click', function () {
        goToStep(item.id);
      });
      nav.appendChild(btn);
    });
    parent.appendChild(nav);
    state.wideTabsEl = nav;
  }

  function updateWideTabs() {
    if (!state.wideTabsEl) {
      return;
    }
    var active = state.steps[state.stepIndex];
    var tabs = state.wideTabsEl.querySelectorAll('.ff-kit-wide__tab');
    for (var i = 0; i < tabs.length; i += 1) {
      tabs[i].classList.toggle('is-active', tabs[i].getAttribute('data-step') === active);
    }
  }

  function appendWideKitStep(body) {
    body.appendChild(stepLabel('Frame only or complete kit?'));
    buildPresetCards(body);
    body.appendChild(stepLabel('Artwork size'));
    var dpoMount = document.createElement('div');
    dpoMount.className = 'ff-wizard__dpo-mount';
    body.appendChild(dpoMount);
    state.dpoMount = dpoMount;
    var help = document.createElement('p');
    help.className = 'ff-kit-addons__help';
    body.appendChild(help);
    state.helpEl = help;
  }

  function appendWideAddons(parent) {
    var cfg = state.config;
    var compBody = document.createElement('div');
    compBody.className = 'ff-kit-addons__components-body';
    (cfg.components || []).forEach(function (comp) {
      compBody.appendChild(buildComponentRow(comp));
    });
    parent.appendChild(compBody);
    if (cfg.mat && !isFloaterPreview()) {
      var matAdd = document.createElement('div');
      matAdd.className = 'ff-kit-addons__matadd';
      matAdd.appendChild(buildMatRow(cfg.mat));
      parent.appendChild(matAdd);
    } else {
      state.mat.cfg = null;
    }
  }

  function appendWideFooter(parent) {
    var cfg = state.config;
    var summary = document.createElement('ul');
    summary.className = 'ff-kit-addons__summary';
    parent.appendChild(summary);
    state.summaryEl = summary;

    var totalRow = document.createElement('div');
    totalRow.className = 'ff-kit-addons__total';
    totalRow.innerHTML =
      '<span>Extra charges</span><span class="ff-kit-addons__total-value">$0.00</span>';
    parent.appendChild(totalRow);
    state.totalEl = totalRow.querySelector('.ff-kit-addons__total-value');

    var upgrade = document.createElement('button');
    upgrade.type = 'button';
    upgrade.className = 'ff-wizard__upgrade';
    upgrade.textContent = 'Want glazing + hardware? Switch to the Complete kit';
    upgrade.addEventListener('click', function () {
      var complete = (state.config.presets || []).filter(function (p) {
        return p.id === 'complete';
      })[0];
      if (complete) {
        applyPreset(complete);
      }
    });
    state.upgradeBtn = upgrade;
    parent.appendChild(upgrade);

    var note = document.createElement('p');
    note.className = 'ff-kit-addons__note';
    note.textContent = cfg.note || '';
    parent.appendChild(note);
    state.noteEl = note;
  }

  function buildWidePagedUi(mode) {
    var mount = state.mount;
    state.dpoMount = null;
    state.actionsMount = null;
    state.panelsById = {};
    state.wideTabsEl = null;

    addLayoutBadge(mount);

    var barReadout = document.createElement('p');
    barReadout.className = 'ff-kit-size-readout ff-kit-size-readout--bar';
    barReadout.hidden = true;
    mount.appendChild(barReadout);
    state.sizeReadoutEl = barReadout;

    if (mode === 'tabs') {
      buildWideTabs(mount, [
        { id: 'kit', label: '1. Kit & size' },
        { id: 'addons', label: '2. Add-ons' },
        { id: 'review', label: '3. Review' }
      ]);
    } else {
      var progress = document.createElement('div');
      progress.className = 'ff-wizard__progress';
      mount.appendChild(progress);
      state.progressEl = progress;
    }

    var panels = document.createElement('div');
    panels.className = 'ff-wizard__panels ff-kit-wide__panels';
    mount.appendChild(panels);

    var s1 = makePanel('kit', 'Kit & size');
    appendWideKitStep(s1.body);
    if (mode === 'pages') {
      s1.body.appendChild(navRow({ next: true, nextLabel: 'Add-ons \u2192' }));
    }
    panels.appendChild(s1.panel);

    var s2 = makePanel('addons', 'Add-ons');
    s2.body.appendChild(stepLabel('Glazing, backing, mat'));
    appendWideAddons(s2.body);
    if (mode === 'pages') {
      s2.body.appendChild(navRow({ back: true, next: true, nextLabel: 'Review \u2192' }));
    }
    panels.appendChild(s2.panel);

    var s3 = makePanel('review', 'Review');
    appendWideFooter(s3.body);
    if (mode === 'pages') {
      var revNav = document.createElement('div');
      revNav.className = 'ff-wizard__nav';
      var back = document.createElement('button');
      back.type = 'button';
      back.className = 'ff-wizard__btn ff-wizard__btn--alt';
      back.innerHTML = '\u2190 Back';
      back.addEventListener('click', goPrev);
      revNav.appendChild(back);
      var toStart = document.createElement('button');
      toStart.type = 'button';
      toStart.className = 'ff-wizard__btn ff-wizard__btn--link';
      toStart.textContent = 'Back to kit & size';
      toStart.addEventListener('click', function () {
        goToStep('kit');
      });
      revNav.appendChild(toStart);
      s3.body.appendChild(revNav);
    }
    panels.appendChild(s3.panel);

    syncSizePrompt(true);
    ensureChips();
    mount.hidden = false;
    state.steps = computeSteps();
    state.stepIndex = 0;
    showStep(true);
    relocateExternals();
  }

  function buildWideColUi() {
    var mount = state.mount;
    state.dpoMount = null;
    state.actionsMount = null;
    state.panelsById = {};
    state.wideTabsEl = null;
    state.progressEl = null;
    state.sizeReadoutEl = null;

    var left = document.createElement('div');
    left.className = 'ff-kit-wide__left';
    addLayoutBadge(left);
    buildSegmentedControl(left);
    left.appendChild(stepLabel('Artwork size'));
    var dpoMount = document.createElement('div');
    dpoMount.className = 'ff-wizard__dpo-mount';
    left.appendChild(dpoMount);
    state.dpoMount = dpoMount;
    var help = document.createElement('p');
    help.className = 'ff-kit-addons__help';
    left.appendChild(help);
    state.helpEl = help;

    var right = document.createElement('div');
    right.className = 'ff-kit-wide__right';
    right.appendChild(stepLabel('Add-ons'));
    appendWideAddons(right);
    appendWideFooter(right);

    mount.appendChild(left);
    mount.appendChild(right);

    syncSizePrompt(true);
    ensureChips();
    mount.hidden = false;
    relocateExternals();
  }

  function scrollToTop() {
    // Bring the top of the product section back into view so the preview (left
    // column on desktop, above the form on mobile) is visible after each step.
    var anchor =
      document.querySelector('.product-detail') ||
      document.querySelector('.product-form') ||
      state.mount;
    if (anchor && anchor.scrollIntoView) {
      try {
        anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (e) {
        anchor.scrollIntoView();
      }
    }
  }

  // Relocate app/theme-owned nodes into the wizard WITHOUT removing them from
  // the product form: the ITORIS DPO container (size + frame options) into step
  // 1, and the native quantity + Add-to-Cart row into the review step. Both are
  // already inside the form, and the wizard host is too, so submission (variant
  // id + properties[Kit ...]) is unaffected. Idempotent; safe to call repeatedly
  // as ITORIS injects asynchronously.
  function relocateExternals() {
    var dpo = document.getElementById('itoris_dynamicproductoptions');
    if (dpo && state.dpoMount && dpo.parentNode !== state.dpoMount) {
      state.dpoMount.appendChild(dpo);
    }
    var summary = document.querySelector('.ff-dimension-summary');
    if (summary && state.dpoMount && summary.parentNode !== state.dpoMount) {
      state.dpoMount.appendChild(summary);
    }
    // Wizard: quantity + Add to cart only appear on the review step. The row
    // stays inside the product form, so submission is unchanged.
    if (state.actionsMount && state.layout === 'wizard') {
      var form = state.mount.closest('form');
      var row = form && form.querySelector('.quantity-submit-row');
      if (row && row.parentNode !== state.actionsMount) {
        state.actionsMount.appendChild(row);
      }
    }
  }

  function buildPresetCards(parent) {
    var cfg = state.config;
    var wrap = document.createElement('div');
    wrap.className = 'ff-kit-addons__presets';

    (cfg.presets || []).forEach(function (preset) {
      var card = document.createElement('button');
      card.type = 'button';
      card.className = 'ff-kit-card';
      card.setAttribute('data-preset', preset.id);

      // Compact cards: name + one-line tagline only (no bullet list) so the
      // 2-up panel stays short.
      card.innerHTML =
        '<span class="ff-kit-card__check" aria-hidden="true"></span>' +
        '<span class="ff-kit-card__name">' + preset.label + '</span>' +
        '<span class="ff-kit-card__tagline">' + (preset.tagline || '') + '</span>';

      card.addEventListener('click', function () {
        applyPreset(preset);
      });

      wrap.appendChild(card);
      state.cards[preset.id] = card;
    });

    (parent || state.mount).appendChild(wrap);
  }

  function buildComponentRow(comp) {
    var row = document.createElement('div');
    row.className = 'ff-kit-row';
    row.setAttribute('data-component', comp.id);

    var head = document.createElement('label');
    head.className = 'ff-kit-row__head';

    var toggle = document.createElement('input');
    toggle.type = 'checkbox';
    toggle.className = 'ff-kit-row__toggle';

    var titles = document.createElement('span');
    titles.className = 'ff-kit-row__titles';
    titles.innerHTML =
      '<span class="ff-kit-row__name">' + comp.label + '</span>' +
      (comp.hint ? '<span class="ff-kit-row__hint">' + comp.hint + '</span>' : '');

    var price = document.createElement('span');
    price.className = 'ff-kit-row__price';

    head.appendChild(toggle);
    head.appendChild(titles);
    head.appendChild(price);
    row.appendChild(head);

    var optionsWrap = document.createElement('div');
    optionsWrap.className = 'ff-kit-row__options';
    optionsWrap.hidden = true;

    var select = document.createElement('select');
    select.className = 'ff-kit-row__select';
    (comp.options || []).forEach(function (opt, idx) {
      var o = document.createElement('option');
      o.value = String(idx);
      o.textContent = opt.label;
      if (opt.default) {
        o.selected = true;
      }
      select.appendChild(o);
    });
    optionsWrap.appendChild(select);
    row.appendChild(optionsWrap);

    toggle.addEventListener('change', function () {
      optionsWrap.hidden = !toggle.checked;
      if (!state.applyingPreset) {
        state.comps[comp.id].userSet = true;
        markCustom();
      }
      recompute();
    });
    select.addEventListener('change', function () {
      if (!state.applyingPreset) {
        state.comps[comp.id].userSet = true;
        markCustom();
      }
      recompute();
    });

    state.comps[comp.id] = { cfg: comp, row: row, enabled: toggle, select: select, optionsWrap: optionsWrap, priceEl: price };
    return row;
  }

  // Step 2 starts on "No matboard" so Continue works without a pick.
  function buildMatChoice() {
    var wrap = document.createElement('div');
    wrap.className = 'ff-kit-addons__presets ff-kit-matchoice';
    var cards = {};
    [
      { id: 'none', name: 'No matboard', tagline: 'Art sits right against the frame' },
      { id: 'mat', name: 'Add a matboard', tagline: 'A border between your art and the frame' }
    ].forEach(function (c) {
      var card = document.createElement('button');
      card.type = 'button';
      card.className = 'ff-kit-card';
      card.setAttribute('data-mat-choice', c.id);
      card.innerHTML =
        '<span class="ff-kit-card__check" aria-hidden="true"></span>' +
        '<span class="ff-kit-card__name">' + c.name + '</span>' +
        '<span class="ff-kit-card__tagline">' + c.tagline + '</span>';
      card.addEventListener('click', function () {
        state.mat.choice = c.id;
        setMatEnabled(c.id === 'mat', true);
      });
      cards[c.id] = card;
      wrap.appendChild(card);
    });
    state.mat.choiceCards = cards;
    var prompt = document.createElement('p');
    prompt.className = 'ff-kit-matchoice__prompt';
    prompt.textContent = 'Choose one to continue.';
    wrap.appendChild(prompt);
    state.mat.choicePrompt = prompt;
    return wrap;
  }

  function paintMatChoice() {
    var cards = state.mat.choiceCards;
    if (!cards) {
      return;
    }
    Object.keys(cards).forEach(function (id) {
      cards[id].classList.toggle('is-selected', id === state.mat.choice);
      cards[id].setAttribute('aria-pressed', id === state.mat.choice ? 'true' : 'false');
    });
    if (state.mat.nextBtn) {
      state.mat.nextBtn.disabled = !state.mat.choice;
    }
    if (state.mat.choicePrompt) {
      state.mat.choicePrompt.hidden = !!state.mat.choice;
    }
  }

  function buildMatRow(matCfg) {
    state.mat.cfg = matCfg;

    var row = document.createElement('div');
    row.className = 'ff-kit-row ff-kit-row--mat';
    row.setAttribute('data-component', 'mat');

    var head = document.createElement('label');
    head.className = 'ff-kit-row__head';

    var toggle = document.createElement('input');
    toggle.type = 'checkbox';
    toggle.className = 'ff-kit-row__toggle';

    var titles = document.createElement('span');
    titles.className = 'ff-kit-row__titles';
    titles.innerHTML =
      '<span class="ff-kit-row__name">Add a matboard <span class="ff-kit-row__optional">(optional)</span></span>' +
      (matCfg.hint ? '<span class="ff-kit-row__hint">' + matCfg.hint + '</span>' : '');

    var price = document.createElement('span');
    price.className = 'ff-kit-row__price';

    head.appendChild(toggle);
    head.appendChild(titles);
    head.appendChild(price);
    row.appendChild(head);

    var optionsWrap = document.createElement('div');
    optionsWrap.className = 'ff-kit-row__options ff-kit-row__options--mat';
    optionsWrap.hidden = true;

    // Border select
    var borderField = document.createElement('label');
    borderField.className = 'ff-kit-mat__field';
    borderField.innerHTML = '<span class="ff-kit-mat__field-label">Border width</span>';
    var borderSelect = document.createElement('select');
    borderSelect.className = 'ff-kit-row__select';
    matBorders().forEach(function (b) {
      var o = document.createElement('option');
      o.value = String(b.value);
      o.textContent = b.label;
      if (b.default) {
        o.selected = true;
      }
      borderSelect.appendChild(o);
    });
    borderField.appendChild(borderSelect);
    optionsWrap.appendChild(borderField);

    // Color swatches
    var colorField = document.createElement('div');
    colorField.className = 'ff-kit-mat__field';
    colorField.innerHTML = '<span class="ff-kit-mat__field-label">Mat color</span>';
    var swatchWrap = document.createElement('div');
    swatchWrap.className = 'ff-kit-mat__swatches';
    matColors().forEach(function (c) {
      var sw = document.createElement('button');
      sw.type = 'button';
      sw.className = 'ff-kit-swatch';
      sw.setAttribute('data-sku', c.sku);
      sw.title = c.name + (c.rate >= 0.04 ? ' (conservation)' : '');
      sw.setAttribute('aria-label', c.name);
      sw.style.backgroundColor = c.swatch;
      if (!isDarkHex(c.swatch)) {
        sw.classList.add('ff-kit-swatch--light');
      }
      sw.addEventListener('click', function () {
        selectMatColor(c.sku, true);
      });
      swatchWrap.appendChild(sw);
    });
    colorField.appendChild(swatchWrap);
    var colorName = document.createElement('span');
    colorName.className = 'ff-kit-mat__color-name';
    colorField.appendChild(colorName);
    optionsWrap.appendChild(colorField);

    row.appendChild(optionsWrap);

    toggle.addEventListener('change', function () {
      setMatEnabled(toggle.checked, true);
    });
    borderSelect.addEventListener('change', function () {
      var b = matBorders().filter(function (x) {
        return String(x.value) === borderSelect.value;
      })[0];
      state.mat.border = b || state.mat.border;
      syncPreviewMat();
      recompute();
    });

    state.mat.toggle = toggle;
    state.mat.optionsWrap = optionsWrap;
    state.mat.borderSelect = borderSelect;
    state.mat.swatchWrap = swatchWrap;
    state.mat.colorNameEl = colorName;
    state.mat.priceEl = price;

    // Seed defaults (not yet enabled).
    state.mat.border = defaultBorder();
    var dc = defaultColor();
    state.mat.colorSku = dc ? dc.sku : null;
    paintSwatchSelection();

    return row;
  }

  function paintSwatchSelection() {
    if (!state.mat.swatchWrap) {
      return;
    }
    var sel = state.mat.swatchWrap.querySelectorAll('.ff-kit-swatch');
    for (var i = 0; i < sel.length; i += 1) {
      var isSel = sel[i].getAttribute('data-sku') === state.mat.colorSku;
      sel[i].classList.toggle('is-selected', isSel);
    }
    if (state.mat.colorNameEl) {
      var c = colorBySku(state.mat.colorSku);
      state.mat.colorNameEl.textContent = c ? c.name : '';
    }
  }

  function selectMatColor(sku, userInitiated) {
    state.mat.colorSku = sku;
    paintSwatchSelection();
    if (userInitiated && !state.applyingPreset) {
      // Selecting a color implies wanting the mat on.
      if (!state.mat.enabled) {
        setMatEnabled(true, true);
        return; // setMatEnabled runs sync + recompute
      }
    }
    syncPreviewMat();
    recompute();
  }

  function setMatEnabled(on, userInitiated) {
    state.mat.enabled = !!on;
    if (state.mat.enabled) {
      state.mat.choice = 'mat';
    }
    paintMatChoice();
    if (state.mat.toggle && state.mat.toggle.checked !== state.mat.enabled) {
      state.mat.toggle.checked = state.mat.enabled;
    }
    if (state.mat.optionsWrap) {
      state.mat.optionsWrap.hidden = !state.mat.enabled;
    }
    if (state.mat.enabled && !state.mat.colorSku) {
      var dc = defaultColor();
      state.mat.colorSku = dc ? dc.sku : null;
      paintSwatchSelection();
    }
    // The matboard is an independent optional add: toggling it does NOT change
    // which kit card is selected (glazing/backing define the card). It's still
    // recorded on the cart line and reflected in the label ("… + matboard").
    syncPreviewMat();
    recompute();
  }

  // ---- Presets -------------------------------------------------------------

  function applyPreset(preset) {
    state.applyingPreset = true;

    var comps = preset.components || {};

    // Glazing / backing style components: enable + set the option by SKU when
    // the preset names one; otherwise disable.
    Object.keys(state.comps).forEach(function (id) {
      var entry = state.comps[id];
      entry.userSet = false;
      var sku = entry.available === false ? null : comps[id];
      if (sku) {
        var idx = 0;
        (entry.cfg.options || []).forEach(function (opt, i) {
          if (opt.sku === sku) {
            idx = i;
          }
        });
        entry.select.value = String(idx);
        entry.enabled.checked = true;
        entry.optionsWrap.hidden = false;
      } else {
        entry.enabled.checked = false;
        entry.optionsWrap.hidden = true;
      }
    });

    // Mat is an independent optional add, so switching presets leaves an
    // already-chosen mat in place. Only a preset that explicitly names a mat
    // turns it on (none currently do).
    if (state.mat.cfg && comps.mat) {
      var wantColor = comps.mat.color;
      if (wantColor && colorBySku(wantColor)) {
        state.mat.colorSku = wantColor;
      } else if (!state.mat.colorSku) {
        var dc = defaultColor();
        state.mat.colorSku = dc ? dc.sku : null;
      }
      if (comps.mat.border) {
        var b = matBorders().filter(function (x) {
          return String(x.value) === String(comps.mat.border);
        })[0];
        if (b) {
          state.mat.border = b;
          if (state.mat.borderSelect) {
            state.mat.borderSelect.value = String(b.value);
          }
        }
      }
      paintSwatchSelection();
      setMatEnabled(true, false);
    }

    // Frame only never shows the mat step, so a mat left on would ride along
    // unseen.
    if (state.layout === 'wizard' && preset.id === 'frame-only') {
      if (state.mat.enabled) {
        setMatEnabled(false, false);
      }
      state.mat.choice = 'none';
      paintMatChoice();
    }

    state.activePreset = preset.id;
    state.basePreset = preset.id;
    state.applyingPreset = false;

    setHelp(
      preset.id === 'complete'
        ? 'Complete kit — glazing, acid-free backing + hanging hardware. Fine-tune or remove them in the Options step.'
        : 'Frame only — just the frame, cut to your size.'
    );
    paintCardSelection();
    syncPreviewMat();
    refreshSteps();
    recompute();
    syncUpgradeVisibility();
  }

  function markCustom() {
    state.activePreset = null;
    setHelp('Custom kit — mix and match glazing, backing & hardware.');
    paintCardSelection();
    refreshSteps();
  }

  function paintCardSelection() {
    Object.keys(state.cards).forEach(function (id) {
      state.cards[id].classList.toggle('is-selected', id === state.activePreset);
    });
    if (state.mount && state.layout === 'compact') {
      var extras = state.mount.querySelector('.ff-kit-flat__extras');
      if (extras) {
        extras.hidden = state.activePreset === 'frame-only';
      }
    }
  }

  function setHelp(text) {
    if (state.helpEl) {
      state.helpEl.textContent = text || '';
    }
  }

  // ---- Chips (mirrored in the preview header) -----------------------------

  function ensureChips() {
    if (state.chipsEl && document.body.contains(state.chipsEl)) {
      return;
    }
    var header = document.querySelector('.ff-frame-preview__header');
    if (!header) {
      return;
    }
    var chips = document.createElement('div');
    chips.className = 'ff-kit-chips';
    header.appendChild(chips);
    state.chipsEl = chips;
  }

  function renderChips(chips) {
    if (!state.chipsEl) {
      return;
    }
    state.chipsEl.innerHTML = '';
    chips.forEach(function (text) {
      var chip = document.createElement('span');
      chip.className = 'ff-kit-chip';
      chip.textContent = text;
      state.chipsEl.appendChild(chip);
    });
    state.chipsEl.hidden = chips.length === 0;
  }

  // ---- Properties ----------------------------------------------------------

  function setProp(key, value) {
    var name = 'properties[' + (PROP_PREFIX + ' ' + key).trim() + ']';
    var input = state.propInputs[key];
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      if (state.formId) {
        input.setAttribute('form', state.formId);
      }
      state.mount.appendChild(input);
      state.propInputs[key] = input;
    }
    if (value === null || value === '' || typeof value === 'undefined') {
      input.disabled = true; // disabled inputs are not serialized to the cart
      input.value = '';
    } else {
      input.disabled = false;
      input.value = String(value);
    }
  }

  // ---- Recompute -----------------------------------------------------------

  function recompute() {
    if (!state.config) {
      return;
    }
    ensureChips();
    applyDpoAvailability();

    var dims = getDims();
    var w = dims.w;
    var h = dims.h;
    var lines = [];
    var chips = [];
    var total = 0;
    var extras = 0; // cost beyond what the chosen kit card includes
    var anySelected = false;
    var anyDpoDriven = !!state.mat.dpo;

    // Glazing / backing components
    (state.config.components || []).forEach(function (comp) {
      var entry = state.comps[comp.id];
      if (!entry) {
        return;
      }
      var inc = includedOption(comp);
      var incPrice = inc ? optionPrice(comp, inc, w, h) : 0;
      labelUpgradeOptions(entry, inc, incPrice, w, h, dims.ready);
      entry.priceEl.classList.remove('is-included', 'is-removed');
      if (!entry.enabled.checked) {
        entry.priceEl.classList.toggle('is-removed', !!inc);
        // Dropping an included item takes its cost back off the price.
        entry.priceEl.textContent = inc && dims.ready ? '\u2212' + money(incPrice) : '';
        entry.priceEl.title = inc ? 'Removed from the kit' : '';
        if (inc) {
          extras -= incPrice;
          lines.push({ label: comp.label, value: 'Removed', priceText: dims.ready ? '\u2212' + money(incPrice) : '' });
        }
        setProp(comp.property, null);
        setProp(comp.property + ' SKU', null);
        setProp(comp.property + ' Price', null);
        return;
      }
      anySelected = true;
      var opt = (comp.options || [])[parseInt(entry.select.value, 10)] || (comp.options || [])[0];
      var price = optionPrice(comp, opt, w, h);
      total += price;
      var extra = inc ? price - incPrice : price;
      extras += extra;
      var priceText;
      if (inc && Math.abs(extra) < 0.005) {
        priceText = 'Included';
      } else if (!dims.ready) {
        priceText = '';
      } else if (inc) {
        priceText = extra > 0 ? '+' + money(extra) + ' upgrade' : '\u2212' + money(-extra);
      } else {
        priceText = '+' + money(price);
      }
      entry.priceEl.textContent = priceText;
      entry.priceEl.classList.toggle('is-included', priceText === 'Included');
      entry.priceEl.title = '';
      lines.push({ label: comp.label, value: opt.label, priceText: priceText || 'enter size' });
      chips.push(comp.id === 'backing' ? 'Backing board (foamboard)' : opt.label);
      if (entry.dpo) {
        // DPO already records this choice on the cart line and adds its product.
        anyDpoDriven = true;
        setProp(comp.property, null);
        setProp(comp.property + ' SKU', null);
        setProp(comp.property + ' Price', null);
        return;
      }
      setProp(comp.property, opt.label);
      setProp(comp.property + ' SKU', opt.sku);
      setProp(comp.property + ' Price', dims.ready ? money(price) : 'sized at checkout');
    });

    // Matboard
    var matCfg = state.mat.cfg || {};
    var matProp = matCfg.property || 'Matboard';
    if (state.mat.enabled && state.mat.colorSku) {
      anySelected = true;
      var color = colorBySku(state.mat.colorSku);
      var matRate = color ? color.rate : 0.03;
      var matPrice = areaPrice(matRate, w, h) + (matCfg.flatFee || 0);
      total += matPrice;
      extras += matPrice;
      var borderLabel = state.mat.border ? state.mat.border.label : '';
      var colorName = color ? color.name : 'Mat';
      var matValue = (borderLabel ? borderLabel + ' ' : '') + colorName + ' mat';
      if (state.mat.priceEl) {
        state.mat.priceEl.textContent = dims.ready ? '+' + money(matPrice) : '';
      }
      lines.push({ label: matCfg.label || 'Matboard', value: matValue, priceText: dims.ready ? '+' + money(matPrice) : 'enter size' });
      chips.push(colorName + ' mat');
      // DPO carries the mat SKU and price; the border width exists only here.
      setProp(matProp, matValue);
      setProp(matProp + ' SKU', state.mat.dpo ? null : color ? color.sku : '');
      setProp(matProp + ' Price', state.mat.dpo ? null : dims.ready ? money(matPrice) : 'sized at checkout');
    } else {
      if (state.mat.priceEl) {
        state.mat.priceEl.textContent = '';
      }
      setProp(matProp, null);
      setProp(matProp + ' SKU', null);
      setProp(matProp + ' Price', null);
    }

    // Kit label + estimate on the cart line. The matboard is orthogonal to the
    // card choice, so append it to whatever bundle is active.
    var kitLabel;
    if (!anySelected) {
      kitLabel = 'Frame only';
    } else if (state.activePreset && state.cards[state.activePreset]) {
      var preset = (state.config.presets || []).filter(function (p) {
        return p.id === state.activePreset;
      })[0];
      kitLabel = preset ? preset.label : 'Custom kit';
      if (state.mat.enabled && state.mat.colorSku) {
        kitLabel += ' + matboard';
      }
    } else {
      kitLabel = 'Custom kit';
    }
    setProp('', kitLabel); // -> properties[Kit]
    var art = getArtworkDims();
    setProp('Artwork Size', art.ready ? inchesText(art.w) + ' x ' + inchesText(art.h) : null);
    setProp('Estimate', anySelected && dims.ready && !anyDpoDriven ? money(total) : null);

    reconcilePresetFromComps();
    syncUpgradeVisibility();
    updateSizeReadout();
    renderSummary(lines, extras, dims.ready);
    renderChips(chips);
    syncDpo();
  }

  function renderSummary(lines, total, ready) {
    var ul = state.summaryEl;
    ul.innerHTML = '';

    if (!lines.length) {
      var empty = document.createElement('li');
      empty.className = 'ff-kit-addons__summary-empty';
      empty.textContent = 'No add-ons selected — just the frame.';
      ul.appendChild(empty);
    } else {
      lines.forEach(function (line) {
        var li = document.createElement('li');
        li.className = 'ff-kit-addons__summary-row';
        li.classList.toggle('is-included', line.priceText === 'Included');
        li.innerHTML =
          '<span class="ff-kit-addons__summary-name">' +
          line.label +
          ': <strong>' +
          line.value +
          '</strong></span>' +
          '<span class="ff-kit-addons__summary-price">' +
          line.priceText +
          '</span>';
        ul.appendChild(li);
      });
    }

    var totalText;
    if (!ready) {
      totalText = 'enter size';
    } else if (Math.abs(total) < 0.005) {
      totalText = lines.length ? 'None \u2014 all included' : 'None';
    } else {
      totalText = total > 0 ? '+' + money(total) : '\u2212' + money(-total);
    }
    state.totalEl.textContent = totalText;
  }

  function basePresetCfg() {
    var id = state.basePreset || state.activePreset;
    return (state.config.presets || []).filter(function (p) {
      return p.id === id;
    })[0] || null;
  }

  // The option the chosen kit card bundles for this component, if any.
  function includedOption(comp) {
    var preset = basePresetCfg();
    var sku = preset && preset.components ? preset.components[comp.id] : null;
    if (!sku) {
      return null;
    }
    return (comp.options || []).filter(function (o) {
      return o.sku === sku;
    })[0] || null;
  }

  function optionPrice(comp, opt, w, h) {
    return comp.pricing === 'each' ? opt.rate || 0 : areaPrice(opt.rate, w, h);
  }

  // Dropdown text: the bundled option says "included", others show the step up.
  function labelUpgradeOptions(entry, inc, incPrice, w, h, ready) {
    var opts = entry.cfg.options || [];
    Array.prototype.forEach.call(entry.select.options, function (o, i) {
      var opt = opts[i];
      if (!opt) {
        return;
      }
      var text = opt.label;
      if (inc && opt === inc) {
        text += ' (included)';
      } else if (inc && ready) {
        var diff = optionPrice(entry.cfg, opt, w, h) - incPrice;
        if (Math.abs(diff) >= 0.005) {
          text += diff > 0 ? ' (+' + money(diff) + ')' : ' (\u2212' + money(-diff) + ')';
        }
      }
      if (o.textContent !== text) {
        o.textContent = text;
      }
    });
  }

  // ---- Wiring --------------------------------------------------------------

  function bind() {
    document.addEventListener('ff:dimensions-changed', function () {
      relocateExternals();
      recompute();
    });

    // ITORIS injects the DPO container asynchronously; keep relocating it (and
    // the Add-to-Cart row) into the wizard until it settles. The submit row is
    // present at load, so it moves on the first tick.
    var attempts = 0;
    var poll = setInterval(function () {
      attempts += 1;
      relocateExternals();
      recompute();
      var dpoIn = state.dpoMount && document.getElementById('itoris_dynamicproductoptions') &&
        document.getElementById('itoris_dynamicproductoptions').parentNode === state.dpoMount;
      if (attempts > 75 || (dpoIn && getDims().ready && allDpoFieldsReady())) {
        clearInterval(poll);
      }
    }, 400);
  }

  function init() {
    state.mount = document.getElementById(MOUNT_ID);
    if (!state.mount) {
      return;
    }
    state.config = parseConfig();
    if (!state.config) {
      return;
    }
    state.formId = state.mount.getAttribute('data-form-id') || null;

    state.mat.cfg = state.config.mat || null;
    syncHiddenMatOptions();

    buildUi();
    bind();

    // Default to Complete kit (first card after the swap). Fall back to the
    // first listed preset if that id is missing.
    var presets = state.config.presets || [];
    var complete = presets.filter(function (p) {
      return p.id === 'complete';
    })[0];
    if (complete) {
      applyPreset(complete);
    } else if (presets.length) {
      applyPreset(presets[0]);
    } else {
      recompute();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
